/**
 * RecipientSearch Component
 *
 * Autocomplete input for selecting message recipients.
 * Fetches org users on type and displays selected users as chips.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { listUsers, type User } from '../../api/users';
import { getErrorMessage } from '../../api/client';

interface SelectedUser {
  id: string;
  name: string;
}

interface RecipientSearchProps {
  selectedUsers: SelectedUser[];
  onSelectionChange: (users: SelectedUser[]) => void;
  excludeUserIds?: string[];
  disabled?: boolean;
}

export default function RecipientSearch({
  selectedUsers,
  onSelectionChange,
  excludeUserIds = [],
  disabled = false,
}: RecipientSearchProps) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search — stable identity so the debounce effect doesn't re-fire on every render
  const searchUsers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // 'ALL': naming someone explicitly must work for archived and
      // deactivated members too — a direct message is exactly how you reach
      // somebody who is off the roster (the archived-member banner promises
      // as much).
      const { users } = await listUsers({ search: query, limit: 10, state: 'ALL' });
      setResults(users);
    } catch (err) {
      setError(getErrorMessage(err));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter out already-selected and excluded users at render time
  const filteredResults = results.filter(u => {
    if (selectedUsers.some(s => s.id === u.id)) return false;
    if (excludeUserIds.includes(u.id)) return false;
    return true;
  });

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchUsers(search);
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [search, searchUsers]);

  const handleSelect = (user: User) => {
    onSelectionChange([
      ...selectedUsers,
      { id: user.id, name: `${user.firstName} ${user.lastName}` },
    ]);
    setSearch('');
    setResults([]);
    inputRef.current?.focus();
  };

  const handleRemove = (userId: string) => {
    onSelectionChange(selectedUsers.filter(u => u.id !== userId));
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    if (search.length >= 2) {
      searchUsers(search);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Recipients
      </label>

      {/* Selected users as chips */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selectedUsers.map(user => (
          <span
            key={user.id}
            className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-800 text-sm"
          >
            {user.name}
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemove(user.id)}
                className="hover:text-primary-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </span>
        ))}
      </div>

      {/* Search input */}
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={handleInputFocus}
        placeholder="Search by name or email..."
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack disabled:bg-gray-100 disabled:cursor-not-allowed"
      />

      {/* Dropdown */}
      {showDropdown && (search.length >= 2 || filteredResults.length > 0) && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {loading && (
            <div className="px-4 py-3 text-sm text-gray-500">
              Searching...
            </div>
          )}

          {error && (
            <div className="px-4 py-3 text-sm text-red-600">
              {error}
            </div>
          )}

          {!loading && !error && filteredResults.length === 0 && search.length >= 2 && (
            <div className="px-4 py-3 text-sm text-gray-500">
              No users found
            </div>
          )}

          {!loading && !error && filteredResults.length > 0 && (
            <ul>
              {filteredResults.map(user => (
                <li key={user.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(user)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3"
                  >
                    <div className="w-8 h-8 bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold">
                      {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
